import React, { useCallback, useEffect, useState } from 'react';
import { fetchMailSubscribers, deleteMailSubscriber } from '../api';

const AdminMailSubscriberManager = ({ user }) => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const loadSubscribers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchMailSubscribers(user.token);
            setSubscribers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user.token]);

    useEffect(() => {
        loadSubscribers();
    }, [loadSubscribers]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this subscriber?')) return;
        try {
            await deleteMailSubscriber(user.token, id);
            setSubscribers(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            alert(err.message || 'Failed to delete');
        }
    };

    const downloadExcel = () => {
        if (!subscribers.length) return;

        // Build CSV content (Excel-compatible)
        const headers = ['S.No', 'Name', 'Email', 'Subject of Interest', 'Registered On'];
        const rows = subscribers.map((s, i) => [
            i + 1,
            `"${(s.name || '').replace(/"/g, '""')}"`,
            `"${(s.email || '').replace(/"/g, '""')}"`,
            `"${(s.subject_of_interest || '').replace(/"/g, '""')}"`,
            `"${new Date(s.createdAt).toLocaleDateString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })}"`,
        ]);

        const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Mail_Subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const filtered = subscribers.filter(s =>
        (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.subject_of_interest || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{
                    width: 40, height: 40, border: '3px solid #f0f0f0',
                    borderTopColor: 'var(--forest)', borderRadius: '50%',
                    margin: '0 auto', animation: 'spin 0.8s linear infinite'
                }} />
                <p style={{ color: 'var(--text3)', marginTop: 16, fontSize: '0.9rem' }}>Loading subscribers...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center', padding: '40px 20px', color: '#dc3545',
                background: '#fff5f5', borderRadius: 12, border: '1px solid #ffcccc'
            }}>
                <p style={{ fontWeight: 600 }}>Error: {error}</p>
                <button onClick={loadSubscribers} style={{
                    marginTop: 12, padding: '8px 20px', background: 'var(--forest)',
                    color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.85rem'
                }}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <style>{`
                .mail-sub-table-responsive { overflow-x: auto; }
                @media (max-width: 768px) {
                    .mail-sub-header-flex { flex-direction: column !important; gap: 12px !important; }
                    .mail-sub-header-flex input { max-width: 100% !important; }
                }
            `}</style>

            {/* Header */}
            <div className="mail-sub-header-flex" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 24, gap: 16, flexWrap: 'wrap'
            }}>
                <div>
                    <h3 style={{
                        fontFamily: "'Playfair Display',serif", fontSize: '1.3rem',
                        fontWeight: 700, color: 'var(--text)', margin: 0
                    }}>
                        E-mail Subscribers
                    </h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text3)', marginTop: 4 }}>
                        {subscribers.length} total subscriber{subscribers.length !== 1 ? 's' : ''}
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        placeholder="Search subscribers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            padding: '9px 16px', borderRadius: 8,
                            border: '1px solid var(--border)', fontFamily: 'inherit',
                            fontSize: '0.85rem', maxWidth: 240, width: '100%',
                            outline: 'none', transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--forest)'}
                        onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                    />
                    <button
                        onClick={downloadExcel}
                        disabled={!subscribers.length}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '9px 18px', background: subscribers.length
                                ? 'linear-gradient(135deg, #217346, #185a34)'
                                : '#ccc',
                            color: 'white', border: 'none', borderRadius: 8,
                            cursor: subscribers.length ? 'pointer' : 'not-allowed',
                            fontWeight: 700, fontSize: '0.85rem', fontFamily: 'inherit',
                            boxShadow: subscribers.length ? '0 3px 12px rgba(33,115,70,0.3)' : 'none',
                            transition: 'all 0.2s', whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            if (subscribers.length) e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download Excel
                    </button>
                </div>
            </div>

            {/* Table */}
            {filtered.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '48px 20px',
                    background: 'var(--cream2)', borderRadius: 16,
                    border: '1px solid var(--border)'
                }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                    <p style={{ fontSize: '0.95rem', color: 'var(--text3)', fontWeight: 600 }}>
                        {searchTerm ? 'No matching subscribers found' : 'No subscribers yet'}
                    </p>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text4)', marginTop: 4 }}>
                        {searchTerm ? 'Try a different search term' : 'Subscribers will appear here when people register'}
                    </p>
                </div>
            ) : (
                <div className="mail-sub-table-responsive" style={{
                    background: 'white', borderRadius: 16, border: '1px solid var(--border)',
                    overflow: 'hidden', boxShadow: 'var(--shadow)'
                }}>
                    <table style={{
                        width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem'
                    }}>
                        <thead>
                            <tr style={{ background: 'var(--cream2)' }}>
                                <th style={thStyle}>#</th>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Subject of Interest</th>
                                <th style={thStyle}>Registered</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((sub, index) => (
                                <tr key={sub.id} style={{
                                    borderBottom: '1px solid var(--border)',
                                    transition: 'background 0.15s'
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cream)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <td style={tdStyle}>{index + 1}</td>
                                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text)' }}>{sub.name}</td>
                                    <td style={tdStyle}>
                                        <a href={`mailto:${sub.email}`} style={{
                                            color: 'var(--forest)', textDecoration: 'none',
                                            fontWeight: 500
                                        }}
                                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                        >{sub.email}</a>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            display: 'inline-block', padding: '3px 10px',
                                            background: 'rgba(28,58,42,0.08)', color: 'var(--forest)',
                                            borderRadius: 50, fontSize: '0.8rem', fontWeight: 700
                                        }}>
                                            {sub.subject_of_interest}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, color: 'var(--text3)', fontSize: '0.82rem' }}>
                                        {new Date(sub.createdAt).toLocaleDateString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric'
                                        })}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleDelete(sub.id)}
                                            title="Remove subscriber"
                                            style={{
                                                background: '#fff5f5', border: '1px solid #ffcccc',
                                                color: '#dc3545', borderRadius: 8,
                                                padding: '5px 12px', cursor: 'pointer',
                                                fontWeight: 600, fontSize: '0.78rem',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = '#dc3545';
                                                e.target.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = '#fff5f5';
                                                e.target.style.color = '#dc3545';
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const thStyle = {
    padding: '12px 16px', textAlign: 'left', fontWeight: 700,
    fontSize: '0.78rem', color: 'var(--text3)', letterSpacing: '0.05em',
    textTransform: 'uppercase', whiteSpace: 'nowrap'
};

const tdStyle = {
    padding: '12px 16px', whiteSpace: 'nowrap'
};

export default AdminMailSubscriberManager;
